/*
 * Aurelia — Product page behaviours
 * Layered on top of the custom elements in global.js (which this file must not
 * touch). Everything here targets the existing PDP markup:
 *   <media-gallery>  — adds click-to-zoom lightbox (arrows + swipe + thumbs)
 *   <variant-picker> — flags unavailable option combinations + live stock state
 *   <product-form>   — mirrored by a sticky add-to-cart bar (no second form id)
 *   [data-size-guide]— accessible drawer modal
 * Dependency-free, progressive-enhancement only. If anything is missing the
 * page degrades to its server-rendered state.
 * ----------------------------------------------------------- */
(function () {
  'use strict';

  var theme = (window.theme = window.theme || {});
  var strings = theme.strings || {};
  var doc = document;

  /* ---------- small shared helpers ---------- */
  function $(sel, root) {
    return (root || doc).querySelector(sel);
  }
  function $all(sel, root) {
    return Array.prototype.slice.call((root || doc).querySelectorAll(sel));
  }

  /* Lightweight focus trap, mirrors the behaviour in global.js without
     reaching into its private scope. Returns a release function. */
  function trapFocus(container, focusEl) {
    var focusable = $all(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      container
    ).filter(function (el) {
      return el.offsetParent !== null || el === doc.activeElement;
    });
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (focusEl && typeof focusEl.focus === 'function') focusEl.focus();
    else if (first) first.focus();
    function onKey(e) {
      if (e.key !== 'Tab' || !first) return;
      if (e.shiftKey && doc.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && doc.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKey);
    return function () {
      container.removeEventListener('keydown', onKey);
    };
  }

  /* =========================================================
   *  Product lightbox — click any gallery image to zoom.
   *  Built as a single reusable overlay appended to <body>.
   * ======================================================= */
  var Lightbox = (function () {
    var el = null;
    var img = null;
    var counter = null;
    var release = null;
    var opener = null;
    var sources = [];
    var index = 0;
    var touchX = null;

    function build() {
      if (el) return;
      el = doc.createElement('div');
      el.className = 'overlay product-lightbox color-scheme color-scheme-1';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', strings.zoomLabel || 'Product image viewer');
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML =
        '<div class="overlay__backdrop" data-lb-close></div>' +
        '<div class="product-lightbox__dialog" data-lb-panel>' +
        '<button type="button" class="product-lightbox__close" data-lb-close aria-label="' +
        escapeAttr(strings.close || 'Close') +
        '">' +
        icon('M6 6l12 12M18 6 6 18') +
        '</button>' +
        '<button type="button" class="product-lightbox__nav product-lightbox__nav--prev" data-lb-prev aria-label="' +
        escapeAttr(strings.previous || 'Previous') +
        '">' +
        icon('M20 12H4M10 6l-6 6 6 6') +
        '</button>' +
        '<figure class="product-lightbox__figure" data-lb-figure>' +
        '<img class="product-lightbox__image" alt="" data-lb-image>' +
        '</figure>' +
        '<button type="button" class="product-lightbox__nav product-lightbox__nav--next" data-lb-next aria-label="' +
        escapeAttr(strings.next || 'Next') +
        '">' +
        icon('M4 12h16M14 6l6 6-6 6') +
        '</button>' +
        '<p class="product-lightbox__counter" data-lb-counter aria-live="polite"></p>' +
        '</div>';
      doc.body.appendChild(el);

      img = $('[data-lb-image]', el);
      counter = $('[data-lb-counter]', el);

      $all('[data-lb-close]', el).forEach(function (b) {
        b.addEventListener('click', close);
      });
      $('[data-lb-prev]', el).addEventListener('click', function () {
        go(index - 1);
      });
      $('[data-lb-next]', el).addEventListener('click', function () {
        go(index + 1);
      });
      // Escape is handled by the document-level listener added in open();
      // here we only deal with arrow-key navigation between images.
      el.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') go(index - 1);
        else if (e.key === 'ArrowRight') go(index + 1);
      });

      // Touch swipe on the figure
      var figure = $('[data-lb-figure]', el);
      figure.addEventListener(
        'touchstart',
        function (e) {
          touchX = e.changedTouches[0].clientX;
        },
        { passive: true }
      );
      figure.addEventListener(
        'touchend',
        function (e) {
          if (touchX === null) return;
          var dx = e.changedTouches[0].clientX - touchX;
          if (Math.abs(dx) > 40) go(dx < 0 ? index + 1 : index - 1);
          touchX = null;
        },
        { passive: true }
      );
    }

    function icon(path) {
      return (
        '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" ' +
        'stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="' +
        path +
        '"/></svg>'
      );
    }

    function open(list, startIndex, openerEl) {
      build();
      sources = list;
      opener = openerEl || null;
      if (sources.length <= 1) el.classList.add('product-lightbox--single');
      else el.classList.remove('product-lightbox--single');
      go(startIndex || 0);
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      doc.body.classList.add('no-scroll');
      doc.addEventListener('keydown', onDocKey);
      release = trapFocus($('[data-lb-panel]', el), $('[data-lb-close]', el));
    }

    function onDocKey(e) {
      if (e.key === 'Escape') close();
    }

    function close() {
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      doc.body.classList.remove('no-scroll');
      doc.removeEventListener('keydown', onDocKey);
      if (release) release();
      if (opener && typeof opener.focus === 'function') opener.focus();
    }

    function go(i) {
      var count = sources.length;
      if (!count) return;
      index = (i + count) % count;
      var item = sources[index];
      img.src = item.src;
      if (item.srcset) img.srcset = item.srcset;
      else img.removeAttribute('srcset');
      img.alt = item.alt || '';
      if (counter) counter.textContent = index + 1 + ' / ' + count;
    }

    return { open: open };
  })();

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  /* ---------- Wire each gallery to the lightbox ---------- */
  function initGalleryZoom(gallery) {
    var viewport = $('[data-gallery-viewport]', gallery);
    if (!viewport) return;
    // Arrows navigate EVERY media item (video/model included)…
    var navItems = $all('.product__media-item[data-media-id]', viewport).filter(function (item) {
      return item.dataset.mediaId !== 'placeholder';
    });
    // …but only still images are zoomable in the lightbox.
    var items = navItems.filter(function (item) {
      return item.querySelector('img');
    });
    if (navItems.length) {
      addGalleryArrows(gallery, viewport, navItems);
      addGalleryScrollSync(gallery, viewport, navItems);
      // Tabs last: its initial select() fires gallery:filtered, which the two
      // above are already listening for.
      addGalleryTabs(gallery, viewport, navItems);
    }
    if (!items.length) return;

    function sources() {
      return items.map(function (item) {
        var im = item.querySelector('img');
        return { src: im.currentSrc || im.src, srcset: im.getAttribute('srcset') || '', alt: im.alt };
      });
    }

    items.forEach(function (item, i) {
      item.classList.add('is-zoomable');
      var im = item.querySelector('img');
      if (im) im.setAttribute('draggable', 'false');
      item.addEventListener('click', function () {
        Lightbox.open(sources(), i, item);
      });
      // keyboard affordance — the item is focusable via CSS/markup below
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label', strings.zoom || 'Zoom image');
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          Lightbox.open(sources(), i, item);
        }
      });
    });
  }

  /* Index of the gallery item sitting nearest the viewport's horizontal
     centre. Shared by the arrows and the pager pill so both read the same
     "which slide am I on" answer. */
  function galleryIndex(viewport, items) {
    var center = viewport.scrollLeft + viewport.clientWidth / 2;
    var best = 0;
    var min = Infinity;
    items.forEach(function (it, i) {
      var d = Math.abs(it.offsetLeft + it.clientWidth / 2 - viewport.offsetLeft - center);
      if (d < min) {
        min = d;
        best = i;
      }
    });
    return best;
  }

  /* Which tab a media item belongs to. Video covers both hosted and external
     video; anything the theme does not know about counts as an image, so a new
     Shopify media type can never fall out of the gallery entirely. */
  function mediaTab(el) {
    var t = el.dataset.mediaType;
    if (t === 'video' || t === 'external_video') return 'video';
    if (t === 'model') return 'model';
    return 'image';
  }

  /* ---------- Media-type tabs (Images / Video / 3D Viewer) ---------- */
  function addGalleryTabs(gallery, viewport, items) {
    var bar = $('[data-gallery-tabs]', gallery);
    if (!bar) return;
    var tabs = $all('[data-media-tab]', bar);
    if (tabs.length < 2) return;
    var thumbs = $all('[data-gallery-thumb]', gallery);

    function select(name, focusSlide) {
      tabs.forEach(function (tab) {
        var on = tab.dataset.mediaTab === name;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      // Filter the track and the thumb strip together, so the thumbs always
      // describe exactly what the slider can reach.
      thumbs.forEach(function (thumb) {
        thumb.hidden = mediaTab(thumb) !== name;
      });
      var first = null;
      items.forEach(function (item) {
        var on = mediaTab(item) === name;
        item.hidden = !on;
        if (on && !first) first = item;
      });
      // Hidden slides leave the track, so land on the new first slide rather
      // than wherever the old scroll offset happens to point.
      if (first) {
        viewport.scrollTo({ left: 0, behavior: focusSlide ? 'smooth' : 'auto' });
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(first.dataset.mediaId, false);
        }
      }
      // Tells the arrows and the thumb sync that the track changed length.
      gallery.dispatchEvent(new CustomEvent('gallery:filtered'));
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        select(tab.dataset.mediaTab, true);
      });
    });

    gallery.classList.add('product__media--has-tabs');
    select(tabs[0].dataset.mediaTab, false);
  }

  /* ---------- Prev/next arrows over the gallery viewport ---------- */
  function addGalleryArrows(gallery, viewport, items) {
    if (items.length < 2 || $('[data-gallery-arrow]', gallery)) return;

    function makeArrow(dir, path, label) {
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'product__media-arrow product__media-arrow--' + dir;
      btn.setAttribute('data-gallery-arrow', dir);
      btn.setAttribute('aria-label', label);
      btn.innerHTML =
        '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" ' +
        'stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="' +
        path +
        '"/></svg>';
      return btn;
    }

    // Recomputed per call: a media-type tab can hide part of the track, and
    // the arrows must step through what is on screen, not the full media list.
    function visible() {
      return items.filter(function (it) {
        return !it.hidden;
      });
    }

    function currentIndex() {
      return galleryIndex(viewport, visible());
    }

    function show(i) {
      var list = visible();
      if (!list.length) return;
      var idx = Math.max(0, Math.min(list.length - 1, i));
      var target = list[idx];
      // <media-gallery> (global.js) exposes setActiveMedia — reuse it so the
      // thumbnail "is-active" state stays in sync with the arrows.
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(target.dataset.mediaId, true);
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    var prev = makeArrow('prev', 'M20 12H4M10 6l-6 6 6 6', strings.previous || 'Previous');
    var next = makeArrow('next', 'M4 12h16M14 6l6 6-6 6', strings.next || 'Next');
    prev.addEventListener('click', function () {
      show(currentIndex() - 1);
    });
    next.addEventListener('click', function () {
      show(currentIndex() + 1);
    });
    // arrows sit inside the gallery; viewport is positioned relative via CSS
    gallery.classList.add('product__media--has-arrows');
    viewport.parentNode.insertBefore(prev, viewport);
    viewport.parentNode.insertBefore(next, viewport.nextSibling);

    function syncArrows() {
      var i = currentIndex();
      prev.toggleAttribute('disabled', i <= 0);
      next.toggleAttribute('disabled', i >= visible().length - 1);
    }
    viewport.addEventListener('scroll', debounce(syncArrows, 80), { passive: true });
    // A tab switch can change the track length without emitting a scroll (the
    // new track may already be at offset 0), so re-sync on the filter event.
    gallery.addEventListener('gallery:filtered', syncArrows);
    syncArrows();
  }

  /* ---------- Keep the thumb strip in step with a swipe ----------
     Thumb/arrow clicks call setActiveMedia themselves; this covers the case
     the gallery never handled — the user swiping the track directly, which
     used to leave the active thumb pointing at the wrong slide. */
  function addGalleryScrollSync(gallery, viewport, items) {
    var strip = $('.product__thumbs', gallery);
    var ticking = false;
    var lastId = null;

    function sync() {
      var list = items.filter(function (it) {
        return !it.hidden;
      });
      if (!list.length) return;
      var current = list[galleryIndex(viewport, list)];
      if (!current || current.dataset.mediaId === lastId) return;
      lastId = current.dataset.mediaId;
      // scroll=false: the user is already looking at it, do not fight them.
      if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(lastId, false);
      }
      if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;
      var thumb = $('[data-gallery-thumb][data-media-id="' + lastId + '"]', strip);
      if (thumb) {
        strip.scrollTo({
          left: thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2,
          behavior: 'smooth'
        });
      }
    }

    viewport.addEventListener(
      'scroll',
      function () {
        // rAF rather than debounce: the active thumb should track the swipe as
        // it happens, not snap into place once the finger stops.
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );
    gallery.addEventListener('gallery:filtered', function () {
      lastId = null;
      sync();
    });
    sync();
  }

  /* =========================================================
   *  Variant availability — flag impossible option combos and
   *  surface a live stock state. Reads the same JSON + radios the
   *  global.js <variant-picker> uses; never mutates them.
   * ======================================================= */
  function initVariantAvailability(picker) {
    var json = $('script[type="application/json"]', picker);
    if (!json) return;
    var variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (e) {
      return;
    }
    if (!variants.length) return;

    var optionEls = $all('.variant-picker__option[data-option-wrap]', picker);
    var stockEl = $('[data-variant-stock]', picker);
    var lowThreshold = parseInt(picker.getAttribute('data-low-stock') || '0', 10);

    function selectedValues() {
      return $all('select, input[type="radio"]:checked', picker).map(function (el) {
        return el.value;
      });
    }

    // For radio pickers: for each option position, test whether choosing a value
    // (while keeping the earlier-position selections) maps to any variant, and
    // whether any such variant is purchasable. A combination that exists but is
    // sold out is dimmed; one that maps to no variant at all is disabled.
    // Mirrors Dawn's availability logic (checks ALL matches, not just first).
    function update() {
      var selected = selectedValues();
      optionEls.forEach(function (optionEl, optIndex) {
        var inputs = $all('input[type="radio"]', optionEl);
        inputs.forEach(function (input) {
          var trial = selected.slice();
          trial[optIndex] = input.value;
          var matches = variants.filter(function (v) {
            // partial match across earlier option positions + this one
            for (var i = 0; i <= optIndex; i++) {
              if (v.options[i] !== trial[i]) return false;
            }
            return true;
          });
          var exists = matches.length > 0;
          var available = matches.some(function (v) {
            return v.available;
          });
          var label = input.nextElementSibling;
          input.classList.toggle('is-unavailable', exists && !available);
          input.classList.toggle('is-nonexistent', !exists);
          if (label) {
            label.classList.toggle('is-unavailable', exists && !available);
            label.classList.toggle('is-nonexistent', !exists);
          }
        });
      });
      updateStock(selected);
    }

    function currentVariant(selected) {
      return variants.find(function (v) {
        return JSON.stringify(v.options) === JSON.stringify(selected);
      });
    }

    function updateStock(selected) {
      if (!stockEl) return;
      var variant = currentVariant(selected || selectedValues());
      var dot = stockEl.querySelector('[data-stock-dot]');
      var label = stockEl.querySelector('[data-stock-label]');
      var state = 'out';
      var text = strings.outOfStock || 'Out of stock';
      if (variant && variant.available) {
        var inv = typeof variant.inventory === 'number' ? variant.inventory : null;
        if (lowThreshold > 0 && inv !== null && inv > 0 && inv <= lowThreshold) {
          state = 'low';
          text = (strings.lowStock || 'Only {{ count }} left').replace('{{ count }}', inv);
        } else {
          state = 'in';
          text = strings.inStock || 'In stock';
        }
      } else if (!variant) {
        state = 'out';
        text = strings.unavailable || 'Unavailable';
      }
      stockEl.dataset.state = state;
      if (dot) dot.dataset.state = state;
      if (label) label.textContent = text;
      stockEl.hidden = false;
    }

    picker.addEventListener('change', debounce(update, 90));
    update();
  }

  /* =========================================================
   *  Sticky add-to-cart bar. Mirrors the real <product-form>:
   *  the bar's button calls requestSubmit() on the canonical form,
   *  so there is exactly one form id on the page and global.js's
   *  ProductForm handles the AJAX add. We just keep the bar's
   *  price/label/image in sync via the variant:change event.
   * ======================================================= */
  function initStickyBar(bar) {
    var sectionId = bar.getAttribute('data-section');
    var productForm = $('product-form[data-section="' + sectionId + '"]');
    var form = productForm ? productForm.querySelector('form') : null;
    var mainButton = productForm ? productForm.querySelector('[data-add-button]') : null;
    if (!form || !mainButton) {
      bar.remove();
      return;
    }

    var barButton = $('[data-sticky-add]', bar);
    var barLabel = barButton ? barButton.querySelector('[data-sticky-label]') : null;
    var barPrice = $('[data-sticky-price]', bar);
    var barImage = $('[data-sticky-image] img', bar);

    // The sticky bar mirrors the page's PRIMARY action, which is Buy it now.
    // Shopify renders that button asynchronously, so look for it at click time
    // rather than at init, and fall back to submitting the form so the bar can
    // never become a dead control.
    if (barButton) {
      barButton.addEventListener('click', function (e) {
        e.preventDefault();
        if (mainButton.getAttribute('aria-disabled') === 'true') return;
        var pay = productForm.querySelector('.shopify-payment-button__button:not([disabled])');
        if (pay) {
          pay.click();
          return;
        }
        if (typeof form.requestSubmit === 'function') form.requestSubmit(mainButton);
        else mainButton.click();
      });
    }

    // Mirror the main button's loading/disabled/label state.
    function syncButton() {
      if (!barButton) return;
      var disabled = mainButton.getAttribute('aria-disabled') === 'true';
      if (disabled) barButton.setAttribute('aria-disabled', 'true');
      else barButton.removeAttribute('aria-disabled');
      var loading = mainButton.classList.contains('is-loading');
      barButton.classList.toggle('is-loading', loading);
      var spinner = barButton.querySelector('.spinner');
      if (spinner) spinner.hidden = !loading;
      if (barLabel) {
        // Sold out / unavailable must propagate from the main button, but while
        // the variant IS buyable the bar keeps its own wording -- it is the Buy
        // it now action, not a second Add to bag.
        var mainLabel = mainButton.querySelector('[data-button-label]');
        var buyLabel = barButton.getAttribute('data-buy-label');
        if (disabled && mainLabel) barLabel.textContent = mainLabel.textContent;
        else if (buyLabel) barLabel.textContent = buyLabel;
        barLabel.style.opacity = loading ? '0' : '1';
      }
    }
    // Watch the main button for state changes (loading spinner, label swaps).
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(syncButton);
      mo.observe(mainButton, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
        attributeFilter: ['aria-disabled', 'class'],
      });
    }

    // Keep price in sync with the variant picker.
    function syncPrice(variant) {
      if (!barPrice) return;
      if (variant && typeof theme.utils !== 'undefined' && theme.utils.formatMoney) {
        var html = '<span class="price__current">' + theme.utils.formatMoney(variant.price) + '</span>';
        if (variant.compare_at_price > variant.price) {
          html += ' <s class="price__compare">' + theme.utils.formatMoney(variant.compare_at_price) + '</s>';
        }
        barPrice.innerHTML = html;
      }
      if (barImage && variant && variant.featured_media) {
        var media = $('.product__media-item[data-media-id="' + variant.featured_media.id + '"] img');
        if (media) {
          barImage.src = media.currentSrc || media.src;
          if (media.getAttribute('srcset')) barImage.srcset = media.getAttribute('srcset');
        }
      }
    }
    // Listen on the section root (not document): dies with the section on
    // theme-editor reloads, and the guard keeps quick-view variant changes
    // from rewriting this bar's price/image with a different product's data.
    var stickyRoot = bar.closest('.shopify-section') || doc;
    stickyRoot.addEventListener('variant:change', function (e) {
      var vp = e.target instanceof Element ? e.target.closest('[data-section]') : null;
      if (!vp || vp.getAttribute('data-section') !== sectionId) return;
      if (e.detail && e.detail.variant) syncPrice(e.detail.variant);
      // give the main button a tick to update, then mirror it
      setTimeout(syncButton, 0);
    });

    syncButton();

    // Reveal the bar once the main buy button scrolls out of view.
    var sentinel = mainButton.closest('.product-form__controls') || mainButton;
    function reveal(visible) {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      // Reserve the bar's real height at the page bottom so it never
      // permanently covers the footer / last block (CSS reads the var).
      doc.documentElement.classList.toggle('sticky-atc-open', visible);
      if (visible) doc.documentElement.style.setProperty('--sticky-atc-height', bar.offsetHeight + 'px');
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          // show the bar when the real add button is NOT on screen
          reveal(!entries[0].isIntersecting);
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0 }
      );
      io.observe(sentinel);
    } else {
      var onScroll = debounce(function () {
        var rect = sentinel.getBoundingClientRect();
        reveal(rect.bottom < 0 || rect.top > window.innerHeight);
      }, 60);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* =========================================================
   *  Size-guide drawer — accessible modal toggled by [data-size-guide-open]
   *  and dismissed by [data-size-guide-close]/backdrop/Escape.
   * ======================================================= */
  function initSizeGuide(modal) {
    var release = null;
    var opener = null;
    var panel = $('[data-overlay-panel]', modal) || modal;

    function open(by) {
      opener = by || null;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      doc.body.classList.add('no-scroll');
      doc.addEventListener('keydown', onKey);
      release = trapFocus(panel, $('[data-size-guide-close]', modal));
    }
    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      doc.body.classList.remove('no-scroll');
      doc.removeEventListener('keydown', onKey);
      if (release) release();
      if (opener && typeof opener.focus === 'function') opener.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
    }

    $all('[data-size-guide-close]', modal).forEach(function (b) {
      b.addEventListener('click', close);
    });
    var backdrop = $('[data-overlay-backdrop]', modal);
    if (backdrop) backdrop.addEventListener('click', close);

    var id = modal.id;
    $all('[data-size-guide-open][aria-controls="' + id + '"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        open(btn);
      });
    });
  }

  /* =========================================================
   *  BuyForMe — "ask someone to gift this." The compose modal has a
   *  LIVE PREVIEW of the gift card + options (name, message, hide price,
   *  button text). "Send on WhatsApp" shares the PRODUCT URL with gift
   *  params (?gift=1&from=&note=&btn=&hp=), so WhatsApp shows a rich
   *  preview and the friend is greeted by initGiftGreeting on open.
   * ======================================================= */
  /* BuyForMe gift-card token codec — URL-safe base64 of a compact JSON, carried
     in the link's #gift= fragment so the recipient's page rebuilds the exact card
     the sender composed (message, name, button, hide-price, chosen image). */
  function bfmEncode(obj) {
    try {
      return btoa(encodeURIComponent(JSON.stringify(obj))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return ''; }
  }
  function bfmDecode(token) {
    try {
      var b64 = String(token).replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      return JSON.parse(decodeURIComponent(atob(b64)));
    } catch (e) { return null; }
  }

  function initBuyForMe(root) {
    var trigger = $('[data-bfm-open]', root);
    var modal = $('[data-bfm-modal]', root);
    if (!trigger || !modal) return;
    var panel = $('[data-bfm-panel]', modal);
    var nameI = $('[data-bfm-name]', modal);
    var msgI = $('[data-bfm-message]', modal);
    var btnI = $('[data-bfm-btn]', modal);
    var hideI = $('[data-bfm-hideprice]', modal);
    var waLink = $('[data-bfm-whatsapp]', modal);
    var nativeBtn = $('[data-bfm-native]', modal);
    var productImage = root.getAttribute('data-product-image');
    var sharedFile = null;
    var imgPick = $('[data-bfm-imgpick]', modal);
    var pvImage = modal.querySelector('.bfm-card__media img');
    var selectedImg = 0;
    var copyBtn = $('[data-bfm-copy]', modal);
    var copyLabel = $('[data-bfm-copy-label]', modal);
    var pvMsg = $('[data-bfm-pv-msg]', modal);
    var pvPrice = $('[data-bfm-pv-price]', modal);
    var pvBtn = $('[data-bfm-pv-btn]', modal);
    var pvFrom = $('[data-bfm-pv-from]', modal);
    var countEl = $('[data-bfm-count]', modal);
    var productUrl = root.getAttribute('data-product-url') || window.location.pathname;
    var selectedVariantId = ''; // kept in sync by the variant:change listener below
    var release = null;

    function giftUrl() {
      var u;
      try { u = new URL(productUrl, window.location.origin); }
      catch (e) { u = new URL(window.location.href); }
      u.hash = '';
      // Carry the sender's selected variant so the friend lands on the exact
      // size/colour the preview card showed (Shopify preselects ?variant=).
      if (selectedVariantId) u.searchParams.set('variant', selectedVariantId);
      var data = {};
      if (nameI && nameI.value.trim()) data.f = nameI.value.trim();
      if (msgI && msgI.value.trim()) data.n = msgI.value.trim();
      if (btnI && btnI.value.trim()) data.b = btnI.value.trim();
      if (hideI && hideI.checked) data.h = 1;
      if (selectedImg) data.i = selectedImg;
      return u.toString() + '#gift=' + bfmEncode(data);
    }
    function syncPreview() {
      if (pvMsg && msgI) pvMsg.textContent = msgI.value || 'Would you gift me this? 💝';
      if (pvBtn && btnI) pvBtn.textContent = btnI.value || 'Buy it for me';
      if (pvPrice) pvPrice.hidden = !!(hideI && hideI.checked);
      if (pvFrom && nameI) {
        var n = nameI.value.trim();
        var fromPrefix = (root.dataset.strFrom || '— with love,') + ' ';
        pvFrom.textContent = n ? fromPrefix + n : '';
        pvFrom.hidden = !n;
      }
      if (countEl && msgI) countEl.textContent = (msgI.value || '').length + ' / 300';
    }
    function syncShare() {
      var text = (msgI ? msgI.value : '') + '\n\n' + giftUrl();
      if (waLink) waLink.href = 'https://wa.me/?text=' + encodeURIComponent(text);
    }
    function refresh() { syncPreview(); syncShare(); }

    // Pre-fetch the product image as a File so native share can attach it
    // synchronously on click (some browsers drop the user-gesture across awaits).
    function prepImage() {
      if (sharedFile || !productImage || !navigator.canShare) return;
      fetch(productImage).then(function (r) { return r.ok ? r.blob() : null; }).then(function (blob) {
        if (!blob) return;
        var file = new File([blob], 'kundan-gift.jpg', { type: blob.type || 'image/jpeg' });
        try { if (navigator.canShare({ files: [file] })) sharedFile = file; } catch (e) { /* files unsupported */ }
      }).catch(function () {});
    }

    function open() {
      refresh();
      prepImage();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      doc.body.classList.add('no-scroll');
      doc.addEventListener('keydown', onKey);
      release = trapFocus(panel, nameI || $('[data-bfm-close]', modal));
    }
    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      doc.body.classList.remove('no-scroll');
      doc.removeEventListener('keydown', onKey);
      if (release) release();
      if (typeof trigger.focus === 'function') trigger.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
    }

    trigger.addEventListener('click', open);
    $all('[data-bfm-close]', modal).forEach(function (b) {
      b.addEventListener('click', close);
    });
    [nameI, msgI, btnI].forEach(function (el) { if (el) el.addEventListener('input', refresh); });
    if (hideI) hideI.addEventListener('change', refresh);
    if (imgPick) {
      $all('[data-bfm-img]', imgPick).forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          $all('[data-bfm-img]', imgPick).forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
          selectedImg = parseInt(thumb.getAttribute('data-img-index'), 10) || 0;
          if (pvImage) { pvImage.removeAttribute('srcset'); pvImage.src = thumb.getAttribute('data-img-src') || pvImage.src; }
          refresh();
        });
      });
    }

    // Keep the preview price synced to the selected variant — scoped to THIS
    // product's picker (quick-view pickers carry a different data-url), and
    // bound to the section root so editor reloads don't stack stale handlers.
    var bfmRoot = root.closest('.shopify-section') || doc;
    bfmRoot.addEventListener('variant:change', function (e) {
      var vp = e.target instanceof Element ? e.target.closest('variant-picker') : null;
      if (!vp || vp.getAttribute('data-url') !== productUrl) return;
      if (e.detail && e.detail.variant) {
        selectedVariantId = e.detail.variant.id;
        if (pvPrice && theme.utils && theme.utils.formatMoney) {
          pvPrice.textContent = theme.utils.formatMoney(e.detail.variant.price);
        }
      }
      syncShare();
    });

    if (copyBtn) {
      var copyTimer = null;
      var copyOrig = '';
      copyBtn.addEventListener('click', function () {
        var link = giftUrl();
        var done = function () {
          if (!copyLabel) return;
          if (copyTimer) clearTimeout(copyTimer);
          else copyOrig = copyLabel.textContent;
          copyLabel.textContent = 'Copied ✓';
          copyTimer = setTimeout(function () {
            copyLabel.textContent = copyOrig;
            copyTimer = null;
          }, 1500);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(link).then(done).catch(done);
        } else {
          var t = doc.createElement('textarea');
          t.value = link;
          doc.body.appendChild(t);
          t.select();
          try { doc.execCommand('copy'); } catch (e) { /* no-op */ }
          doc.body.removeChild(t);
          done();
        }
      });
    }

    // Native Web Share (mobile) — attaches the actual product photo and opens
    // the OS share sheet (WhatsApp, Instagram, Messages…). Falls back to the
    // WhatsApp link + Copy below when unsupported (e.g. most desktops).
    if (nativeBtn && navigator.share) {
      nativeBtn.hidden = false;
      if (waLink) { waLink.classList.remove('button--primary'); waLink.classList.add('button--secondary'); }
      nativeBtn.addEventListener('click', function () {
        var url = giftUrl();
        var data = {
          title: root.getAttribute('data-product-title') || document.title,
          text: (msgI ? msgI.value : '') + '\n\n' + url,
          url: url
        };
        if (sharedFile) {
          try { if (navigator.canShare && navigator.canShare({ files: [sharedFile] })) data.files = [sharedFile]; } catch (e) { /* text-only */ }
        }
        navigator.share(data).catch(function () { /* user dismissed */ });
      });
    }
  }

  /* =========================================================
   *  Gift greeting — when a product page is opened via a BuyForMe link
   *  (?gift=1), greet the visitor with the personalised gift card. The
   *  CTA closes the card and scrolls to the buy buttons so they can buy.
   * ======================================================= */
  function initGiftGreeting(node) {
    var data = null;
    var hashMatch = window.location.hash.match(/gift=([^&]+)/);
    if (hashMatch) data = bfmDecode(decodeURIComponent(hashMatch[1]));
    if (!data) {
      // Backward-compat: earlier links used ?gift=1&from=&note=&btn=&hp=
      var params = new URLSearchParams(window.location.search);
      if (params.get('gift') === '1') {
        data = { f: params.get('from'), n: params.get('note'), b: params.get('btn'), h: params.get('hp') === '1' ? 1 : 0 };
      }
    }
    if (!data) {
      if (node.parentNode) node.parentNode.removeChild(node);
      return;
    }
    var panel = $('[data-gg-panel]', node);
    var msgEl = $('[data-gg-msg]', node);
    var priceEl = $('[data-gg-price]', node);
    var fromEl = $('[data-gg-from]', node);
    var ctaEl = $('[data-gg-cta]', node);
    var imgEl = node.querySelector('.gift-greeting__media img');
    var mediaEl = $('[data-gg-media]', node);
    var release = null;

    if (msgEl) msgEl.textContent = data.n || 'Someone special is hoping you’ll gift them this 💝';
    if (data.h && priceEl) priceEl.hidden = true;
    if (data.b && ctaEl) ctaEl.textContent = data.b;
    if (data.f && fromEl) {
      var fromPrefix = (node.dataset.strFrom || '— with love,') + ' ';
      fromEl.textContent = fromPrefix + data.f;
      fromEl.hidden = false;
    }
    if (data.i && imgEl && mediaEl) {
      try {
        var media = JSON.parse(mediaEl.textContent || '[]');
        if (media[data.i]) { imgEl.removeAttribute('srcset'); imgEl.src = media[data.i]; }
      } catch (e) { /* ignore malformed media json */ }
    }

    function close() {
      node.classList.remove('is-open');
      node.setAttribute('aria-hidden', 'true');
      doc.body.classList.remove('no-scroll');
      doc.removeEventListener('keydown', onKey);
      if (release) release();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    function open() {
      node.classList.add('is-open');
      node.setAttribute('aria-hidden', 'false');
      doc.body.classList.add('no-scroll');
      doc.addEventListener('keydown', onKey);
      release = trapFocus(panel, ctaEl);
    }

    $all('[data-gg-close]', node).forEach(function (b) {
      b.addEventListener('click', close);
    });
    if (ctaEl) {
      ctaEl.addEventListener('click', function () {
        close();
        var target = $('product-form') || $('[data-add-button]') || $('.product-info');
        if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    setTimeout(open, 350);
  }

  /* =========================================================
   *  ELRA PDP — coupon banner. Copies the merchant-set coupon code to the
   *  clipboard (Clipboard API with an execCommand fallback) and swaps the
   *  button label to "Copied!" for 1.5s. All copy/price/code text is
   *  server-rendered from block settings; this only wires the button.
   * ======================================================= */
  function initPdpCoupon(root) {
    var btn = $('[data-pdp-coupon-copy]', root);
    var label = $('[data-pdp-coupon-copy-label]', root);
    if (!btn) return;
    var code = btn.getAttribute('data-copy-value') || '';

    function fallbackCopy(text) {
      var t = doc.createElement('textarea');
      t.value = text;
      t.setAttribute('readonly', '');
      t.style.position = 'fixed';
      t.style.opacity = '0';
      doc.body.appendChild(t);
      t.select();
      try {
        doc.execCommand('copy');
      } catch (e) {
        /* no-op — nothing more we can do without clipboard permissions */
      }
      doc.body.removeChild(t);
    }

    var copiedTimer = null;
    var copiedOrig = '';
    function showCopied() {
      btn.classList.add('is-copied');
      if (label) {
        // Snapshot only when idle — a double-click must not capture "Copied!"
        if (copiedTimer) clearTimeout(copiedTimer);
        else copiedOrig = label.textContent;
        label.textContent = 'Copied!';
      } else if (copiedTimer) {
        clearTimeout(copiedTimer);
      }
      copiedTimer = setTimeout(function () {
        btn.classList.remove('is-copied');
        if (label) label.textContent = copiedOrig;
        copiedTimer = null;
      }, 1500);
    }

    btn.addEventListener('click', function () {
      if (!code) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(code)
          .then(showCopied)
          .catch(function () {
            fallbackCopy(code);
            showCopied();
          });
      } else {
        fallbackCopy(code);
        showCopied();
      }
    });
  }

  /* =========================================================
   *  ELRA PDP — pincode / delivery checker. Fully client-side: validates a
   *  6-digit Indian pincode and, if the block lists serviceable pincodes,
   *  checks membership. No network request is made.
   * ======================================================= */
  function initPdpPincode(root) {
    var input = $('[data-pincode-input]', root);
    var btn = $('[data-pincode-check]', root);
    var result = $('[data-pincode-result]', root);
    if (!input || !btn || !result) return;

    var serviceable = (root.getAttribute('data-serviceable') || '')
      .split(',')
      .map(function (p) {
        return p.trim();
      })
      .filter(Boolean);

    // Copy is server-rendered onto the root via data-str-* (block settings); these
    // literals are only a defensive fallback for markup rendered before this wiring.
    var strSuccess = root.dataset.strSuccess || '✓ Delivers to {pincode} in 3–6 working days · COD available';
    var strInvalid = root.dataset.strInvalid || 'Please enter a valid 6-digit pincode.';
    var strUnserviceable = root.dataset.strUnserviceable || 'Sorry, not serviceable at this pincode.';

    function setResult(text, state) {
      result.textContent = text;
      result.classList.remove('is-success', 'is-error');
      if (state) result.classList.add(state);
    }

    function check() {
      var value = (input.value || '').trim();
      if (!/^\d{6}$/.test(value)) {
        setResult(strInvalid.replace('{pincode}', value), 'is-error');
        return;
      }
      if (serviceable.length && serviceable.indexOf(value) === -1) {
        setResult(strUnserviceable.replace('{pincode}', value), 'is-error');
        return;
      }
      setResult(strSuccess.replace('{pincode}', value), 'is-success');
    }

    input.addEventListener('input', function () {
      input.value = input.value.replace(/[^0-9]/g, '').slice(0, 6);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        check();
      }
    });
    btn.addEventListener('click', check);
  }

  /* ---------- tiny debounce (global.js's is private) ---------- */
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /* =========================================================
   *  Recently viewed — store the current product handle in
   *  localStorage and hydrate any [data-recently-viewed] rail
   *  via the Shopify product-recommendations / search endpoint.
   * ======================================================= */
  function trackRecentlyViewed() {
    var meta = $('[data-product-handle]');
    if (!meta || !window.localStorage) return;
    var handle = meta.getAttribute('data-product-handle');
    if (!handle) return;
    var key = 'aurelia:recently-viewed';
    var list = [];
    try {
      list = JSON.parse(window.localStorage.getItem(key) || '[]');
    } catch (e) {
      list = [];
    }
    list = list.filter(function (h) {
      return h !== handle;
    });
    list.unshift(handle);
    list = list.slice(0, 12);
    try {
      window.localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {
      /* quota / privacy mode — non-fatal */
    }
  }

  function initRecentlyViewed(section) {
    if (!window.localStorage) {
      section.remove();
      return;
    }
    var currentHandle = section.getAttribute('data-current-handle') || '';
    var key = 'aurelia:recently-viewed';
    var list = [];
    try {
      list = JSON.parse(window.localStorage.getItem(key) || '[]');
    } catch (e) {
      list = [];
    }
    list = list.filter(function (h) {
      return h && h !== currentHandle;
    });
    if (!list.length) {
      section.remove();
      return;
    }
    var grid = $('[data-recently-viewed-grid]', section);
    if (!grid) {
      section.remove();
      return;
    }

    // Each stored handle's card is fetched from the section-rendering API
    // (data-card-url has a __handle__ placeholder). Requesting a product URL
    // with ?section_id renders this section with that product in scope, so the
    // response contains exactly one .product-card we can lift out.
    var base = section.getAttribute('data-card-url');
    if (!base) {
      section.remove();
      return;
    }

    var fetched = 0;
    var collected = [];
    var want = Math.min(list.length, parseInt(section.getAttribute('data-limit') || '4', 10));

    list.slice(0, want).forEach(function (handle, i) {
      fetch(base.replace('__handle__', encodeURIComponent(handle)))
        .then(function (r) {
          return r.ok ? r.text() : '';
        })
        .then(function (html) {
          if (html) {
            var docFrag = new DOMParser().parseFromString(html, 'text/html');
            var card = docFrag.querySelector('.product-card');
            if (card) collected[i] = card.outerHTML;
          }
        })
        .catch(function () {})
        .then(function () {
          fetched++;
          if (fetched === want) {
            var markup = collected.filter(Boolean).join('');
            if (markup) {
              grid.innerHTML = markup;
              section.hidden = false;
              section.classList.add('is-loaded');
            } else {
              section.remove();
            }
          }
        });
    });
  }

  /* ---------- Init (re-runnable for the theme editor) ---------- */
  function init(root) {
    var scope = root || doc;
    $all('media-gallery', scope).forEach(initGalleryZoom);
    $all('variant-picker', scope).forEach(initVariantAvailability);
    $all('[data-sticky-bar]', scope).forEach(initStickyBar);
    $all('[data-size-guide]', scope).forEach(initSizeGuide);
    $all('[data-buy-for-me]', scope).forEach(initBuyForMe);
    $all('[data-gift-greeting]', scope).forEach(initGiftGreeting);
    $all('[data-recently-viewed]', scope).forEach(initRecentlyViewed);
    $all('[data-pdp-coupon]', scope).forEach(initPdpCoupon);
    $all('[data-pincode]', scope).forEach(initPdpPincode);
  }

  function boot() {
    trackRecentlyViewed();
    init();
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init the relevant block when edited in the theme editor.
  doc.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
